import { computed, onBeforeMount, ref, toValue } from 'vue';
import i18n, { getCurrentLocale } from '@/i18n';
import omit from 'lodash/omit';
import round from 'lodash/round';
import { DateTime } from 'luxon';
import {
  OCCUPANCIES,
  AGENCY_SELLING_PRICE,
  AGENCY_TAX,
  MARGIN_ADJUSTMENT,
  TOTAL_MARGIN,
  HOTEL_EBB,
  ACV_EBB,
  PF_DISCOUNT,
  LAND_COST,
  FL_COST,
  COST_ADJUSTMENT,
  FL_COST_OFFSET,
  FL_COST_ADJUSTMENT,
  TOTAL_COST,
  MAP_DEPARTMENT_TO_ROLE,
  GQT_ADMIN,
  GQT_SUPERVISOR,
  OUTBOUND,
  INBOUND,
  HOTEL_COST_ADJ,
  ARD_BASE_FARE,
  ARD_TAXES,
  NET_MARGIN,
  SUPPLIER_COST,
  SUPPLIER_TAX,
  SUPPLIER_SUPPLEMENT,
  SUPPLIER_COST_CAD,
  HOTEL_COST_CAD,
  SUFFIX_PUBLIC,
  SUFFIX_GROUPB,
  SUFFIX_VACV,
  SUFFIX_AGENCY_SELLING_PRICE,
  SUFFIX_TAX,
  SUFFIX_TOTAL_SELLING,
  SUFFIX_CHARTER
} from '@/constants';
import { getEnvVar } from '@/utils/env';
import { useAirManagementStore } from '@/store/AirManagementStore';
import { UserInfo } from '@/modules/user/UserInfo';
import { useUserAuthorizationsStore } from '@/store/UserAuthorizationsStore';
import { useUserInfo } from '@/modules/user/useUserInfo';
import { cloneDeep } from 'lodash';
import { getTextVariant } from '@/utils/stringUtils';
import { useToast } from 'primevue/usetoast';
import { useModalComposable } from '@/views/composable';

export function useWorksheetComposable() {
  const { handleOpenErrorMessage } = useModalComposable();
  const toast = useToast();
  const PRICE_KEY = {
    HEADER: 'priceHeader'
  };
  const currentLanguage = getCurrentLocale();
  const BASE_URL = getEnvVar('VITE_CDN_BASE_URL');
  const loadingIcon = `${BASE_URL}/assets/images/loading.png`;
  const semistarImage = `${BASE_URL}/assets/images/semistar.svg`;
  const currentUser = ref<UserInfo>();
  const ECONOMY_TYPE = 'Economy';
  const BUSINESS_TYPE = 'Business';
  type RowCalculationMap = Record<string, string>;

  const LIST_CABIN_TYPE = [
    {
      name: i18n.global.t('worksheet.economy_class'),
      data: ECONOMY_TYPE
    },
    {
      name: i18n.global.t('worksheet.business'),
      data: BUSINESS_TYPE
    }
  ];

  const titleHotel = [
    { key: 'sellingPrice', name: i18n.global.t('worksheet.agency_selling_price') },
    { key: 'costAdj', name: i18n.global.t('worksheet.cost_adjustment') },
    { key: 'ebbAcv', name: i18n.global.t('worksheet.acv_ebb') },
    { key: 'landCost', name: i18n.global.t('worksheet.land_cost') },
    { key: 'marAdj', name: i18n.global.t('worksheet.margin_adjustment') },
    { key: 'pfDis', name: i18n.global.t('worksheet.pf_discounts') },
    { key: 'taxes', name: i18n.global.t('worksheet.agency_taxes') },
    { key: 'totalCost', name: i18n.global.t('worksheet.total_cost') },
    { key: 'flCostOffset', name: i18n.global.t('worksheet.fl_cost_offset') },
    { key: 'totalMar', name: i18n.global.t('worksheet.total_margin') },
    { key: 'ebbHotel', name: i18n.global.t('worksheet.hotel_ebb') },
    { key: 'ebbTour', name: i18n.global.t('worksheet.tour_ebb') },
    { key: 'flCost', name: i18n.global.t('worksheet.fl_cost') },
    { key: 'flCostAdjustment', name: i18n.global.t('worksheet.fl_cost_adjustment') },
    { key: 'priceSummaryTitle', name: i18n.global.t('worksheet.selling_price_summary') },
    { key: 'adjustmentSummaryTitle', name: i18n.global.t('worksheet.adjustment_summary') },
    { key: 'costSummaryTitle', name: i18n.global.t('worksheet.cost_summary') },
    { key: 'priceSummary', name: i18n.global.t('worksheet.total_selling') },
    { key: 'adjustmentSummary', name: i18n.global.t('worksheet.total_adjustment') },
    { key: 'costSummary', name: i18n.global.t('worksheet.total_cost') },
    { key: HOTEL_COST_ADJ, name: i18n.global.t('worksheet.hotelCostAdj') },
    { key: 'additionInformation', name: i18n.global.t('role.additional_information') },
    { key: 'ardBaseFare', name: i18n.global.t('permisson.ard_base_fare') },
    { key: 'ardTaxes', name: i18n.global.t('permisson.ard_tax') },
    { key: 'netMargin', name: i18n.global.t('permisson.net_margin') }
  ];

  const titleCostAdjustmentTool = [
    { key: 'supplierCost', name: i18n.global.t('worksheet.supplier_cost') },
    { key: 'supplierTax', name: i18n.global.t('worksheet.supplier_tax') },
    { key: 'supplierSupplement', name: i18n.global.t('worksheet.supplier_supplement') },
    { key: 'supplierTotal', name: i18n.global.t('worksheet.supplier_total') },
    { key: 'supplierCostCAD', name: i18n.global.t('worksheet.supplier_cost_CAD') },
    { key: 'hotelCostCAD', name: i18n.global.t('worksheet.hotel_cost_CAD') },
    { key: 'costAdj', name: i18n.global.t('worksheet.cost_adjustment') }
  ];

  onBeforeMount(() => {
    useUserInfo()
      .getUserInfo()
      .then((r) => (currentUser.value = r));
  });

  const renderPriceSummary = (permissionList) => {
    const priceSummary = [];
    if (permissionList?.agency_selling_price_view) {
      priceSummary.push({
        key: AGENCY_SELLING_PRICE,
        canEdit: permissionList?.agency_selling_price_edit
      });
    }
    if (permissionList?.agency_tax_view) {
      priceSummary.push({
        key: AGENCY_TAX,
        canEdit: permissionList?.agency_tax_edit
      });
    }
    return {
      headerName: 'priceSummaryTitle',
      name: 'priceSummary',
      list: priceSummary
    };
  };

  const renderAdjustmentSummary = (permissionList) => {
    const adjustmentSummary = [];
    if (permissionList?.margin_adj_view) {
      adjustmentSummary.push({
        key: MARGIN_ADJUSTMENT,
        canEdit: permissionList?.margin_adj_edit
      });
    }
    if (permissionList?.rm_margin_adj_view) {
      adjustmentSummary.push({
        key: MARGIN_ADJUSTMENT,
        canEdit: permissionList?.rm_margin_adj_edit
      });
    }
    adjustmentSummary.push({
      key: HOTEL_COST_ADJ,
      canEdit: false
    });
    adjustmentSummary.push({
      key: FL_COST_OFFSET,
      canEdit: false
    });
    if (permissionList?.total_margin_view) {
      adjustmentSummary.push({
        key: TOTAL_MARGIN,
        canEdit: permissionList?.total_margin_edit
      });
    }
    if (permissionList?.margin_view) {
      adjustmentSummary.push({
        key: TOTAL_MARGIN,
        canEdit: permissionList?.margin_edit
      });
    }
    if (permissionList?.ebb_view) {
      adjustmentSummary.push({
        key: HOTEL_EBB,
        canEdit: permissionList?.ebb_edit
      });
    }
    if (permissionList?.tour_ebb_view) {
      adjustmentSummary.push({
        key: HOTEL_EBB,
        canEdit: permissionList?.tour_ebb_edit
      });
    }
    if (permissionList?.acv_ebb_view) {
      adjustmentSummary.push({
        key: ACV_EBB,
        canEdit: permissionList?.acv_ebb_edit
      });
    }
    if (permissionList?.pf_discount_view) {
      adjustmentSummary.push({
        key: PF_DISCOUNT,
        canEdit: permissionList?.pf_discount_edit
      });
    }
    return {
      headerName: 'adjustmentSummaryTitle',
      name: 'adjustmentSummary',
      noTotal: true,
      list: adjustmentSummary
    };
  };

  const renderCostSummary = (permissionList) => {
    const costSummary = [];
    if (permissionList?.land_cost_view) {
      costSummary.push({
        key: LAND_COST,
        canEdit: permissionList?.land_cost_edit
      });
    }
    if (permissionList?.fl_cost_view) {
      costSummary.push({
        key: FL_COST,
        canEdit: permissionList?.fl_cost_edit
      });
    }
    if (permissionList?.fl_cost_adj_view) {
      costSummary.push({
        key: FL_COST_ADJUSTMENT,
        canEdit: false
      });
    }
    if (permissionList?.cost_adj_view) {
      costSummary.push({
        key: COST_ADJUSTMENT,
        canEdit: permissionList?.cost_adj_edit
      });
    }
    return {
      headerName: 'costSummaryTitle',
      name: 'costSummary',
      list: costSummary
    };
  };

  const renderAdditionInformation = (permissionList) => {
    const costSummary = [];
    if (permissionList?.ard_base_fare) {
      costSummary.push({
        key: ARD_BASE_FARE,
        canEdit: false
      });
    }
    if (permissionList?.ard_tax) {
      costSummary.push({
        key: ARD_TAXES,
        canEdit: false
      });
    }
    if (permissionList?.net_margin) {
      costSummary.push({
        key: NET_MARGIN,
        canEdit: false
      });
    }
    return {
      headerName: 'additionInformation',
      name: 'additionInformation',
      list: costSummary,
      noTotal: true
    };
  };

  const renderSupplierCostSummary = (permissionList: any) => {
    const supplierCostTotal = [];
    supplierCostTotal.push({
      key: SUPPLIER_COST,
      canEdit: permissionList.cost_calculation_edit
    });
    supplierCostTotal.push({
      key: SUPPLIER_TAX,
      canEdit: permissionList.cost_calculation_edit
    });
    supplierCostTotal.push({
      key: SUPPLIER_SUPPLEMENT,
      canEdit: permissionList.cost_calculation_edit
    });
    return {
      headerName: 'supplierTotalTitle',
      name: 'supplierTotal',
      list: supplierCostTotal
    };
  };

  const renderSupplierCostCADSummary = () => {
    const costAdjustmentSummary = [];
    costAdjustmentSummary.push({
      key: SUPPLIER_COST_CAD,
      canEdit: false
    });
    costAdjustmentSummary.push({
      key: HOTEL_COST_CAD,
      canEdit: false
    });
    return {
      headerName: 'costAdjustmentTile',
      name: 'costAdj',
      list: costAdjustmentSummary
    };
  };

  const filterPermission = (permissionList, permissonAddition) => {
    const priceDataList = [];
    priceDataList.push(renderPriceSummary(permissionList));
    priceDataList.push(renderAdjustmentSummary(permissionList));
    priceDataList.push(renderCostSummary(permissionList));
    if (permissonAddition?.view_additional_section) {
      priceDataList.push(renderAdditionInformation(permissonAddition));
    }
    return priceDataList;
  };

  const filterPermissionCostAdjusmentTool = (permissionList: any) => {
    const priceDataList = [];
    priceDataList.push(renderSupplierCostSummary(permissionList));
    priceDataList.push(renderSupplierCostCADSummary());
    return priceDataList;
  };

  const calculateListPrice = (listPrice, getKey) => {
    return listPrice.reduce((total, currentValue) => Number(currentValue[getKey]) + total, 0);
  };
  const filterPriceList = (priceList, permissionList, permissonAddition?) => {
    const priceColumn = filterPermission(permissionList, permissonAddition);
    const keyList = priceList?.sellingPrice ? Object.keys(priceList?.sellingPrice) : [];
    const newList = priceColumn.map((listPrice) => {
      const noTotal = listPrice.noTotal;
      if (listPrice.list.length) {
        const groupName = listPrice.name;
        const groupHeader = listPrice.headerName;

        listPrice = listPrice.list.map((item) => {
          return {
            ...priceList[item.key],
            rowName: item.key,
            canEdit: item.canEdit
          };
        });
        if (!noTotal) {
          const data = {};
          let childArray = [];
          keyList.forEach((key) => {
            if (key === 'childPrices') {
              childArray = pushChildPrices(listPrice);
              const totalArrayChild = responseTotalArrayChild(childArray);
              data[key] = totalArrayChild;
            } else {
              data[key] = calculateListPrice(listPrice, key);
            }
          });
          data.key = TOTAL_COST;
          data.rowName = groupName;
          listPrice.push(data);
        }

        listPrice.unshift({
          key: PRICE_KEY.HEADER,
          rowName: groupHeader
        });
        return listPrice;
      }
      return [];
    });
    const result = newList.reduce((total, currentValue) => {
      return [...total, ...currentValue];
    }, []);
    const priceListOccupancies = cloneDeep(priceList[OCCUPANCIES]);

    return [{ rowName: OCCUPANCIES, ...priceListOccupancies, canEdit: canEditAgeRanges }, ...result];
  };

  const calculateCostAdjustmentForChild = (childArray: any) => {
    if (!Array.isArray(childArray)) {
      return [];
    }
    const array = childArray?.map((child) => {
      const arrayNew = child?.childPrices?.map((value) => value ?? value);
      return {
        childPrices: arrayNew
      };
    });
    if (array[0]?.childPrices) {
      return calculatedTotal(array);
    }
    return [];
  };

  const calculatedTotal = (array: any) => {
    const length = array[0].childPrices.length;
    const result = [];
    let calValue = 0;
    for (let i = 0; i < length; i++) {
      switch (array.length) {
        case 3:
          calValue = +array[0].childPrices[i] * (1 + +array[1].childPrices[i] / 100) + +array[2].childPrices[i];
          break;
        case 2:
          calValue = round(array[0].childPrices[i] - array[1].childPrices[i]);
          break;
      }
      result.push(!isNaN(calValue) ? calValue : 'N/A');
    }

    return result;
  };

  const calculateListSupplier = (listPrice: any, getKey: any) => {
    let calculateSupplier = 0;
    const getSuppliplyCost = listPrice.find((price) => price.rowName == 'supplierCost');
    const getSuppliplyTax = listPrice.find((price) => price.rowName == 'supplierTax');
    const getSuppliplySupplement = listPrice.find((price) => price.rowName == 'supplierSupplement');
    const getSupplierCostCAD = listPrice.find((price) => price.rowName == 'supplierCostCAD');
    const getHotelCostCAD = listPrice.find((price) => price.rowName == 'hotelCostCAD');
    switch (listPrice.length) {
      case 3:
        calculateSupplier = +getSuppliplyCost[getKey] * (1 + +getSuppliplyTax[getKey] / 100) + +getSuppliplySupplement[getKey];
        break;
      case 2:
        calculateSupplier = round(getSupplierCostCAD[getKey] - getHotelCostCAD[getKey]);
        break;
    }
    return !isNaN(calculateSupplier) ? calculateSupplier.toFixed(2) : 'N/A';
  };

  const filterCostAdjustment = (priceList: any, permissionList: any) => {
    const priceColumn = filterPermissionCostAdjusmentTool(permissionList);
    const keyList = priceList?.supplierCost ? Object.keys(priceList?.supplierCost) : [];
    const newList = priceColumn.map((price) => {
      const noTotal = price.noTotal;
      if (price.list.length) {
        const groupHeader = price.headerName;
        const groupName = price.name;
        price = price.list.map((item) => {
          return {
            ...priceList[item.key],
            canEdit: item.canEdit,
            rowName: item.key
          };
        });
        if (!noTotal) {
          let childArray = [];
          const data = {};
          keyList.forEach((key) => {
            if (key === 'childPrices') {
              childArray = pushChildPrices(price);
              const totalArrayChild = calculateCostAdjustmentForChild(childArray);
              data[key] = totalArrayChild;
            } else {
              data[key] = calculateListSupplier(price, key);
            }
          });
          data.key = TOTAL_COST;
          data.rowName = groupName;
          price.push(data);
        }

        if (groupHeader !== 'supplierTotalTitle') {
          price.unshift({
            key: PRICE_KEY.HEADER,
            rowName: groupHeader
          });
        }
        return price;
      }
      return [];
    });
    const result = newList.reduce((total, currentValue) => {
      return [...total, ...currentValue];
    }, []);
    return [{ rowName: OCCUPANCIES, ...priceList[OCCUPANCIES], canEdit: true }, ...result];
  };

  const responseTotalArrayChild = (childArray) => {
    if (!Array.isArray(childArray)) {
      return [];
    }
    const array = childArray?.map((m) => {
      const arrayNew = m?.childPrices?.map((value) => (value === null ? 0 : value));
      return {
        childPrices: arrayNew
      };
    });
    if (array[0]?.childPrices) {
      const childArrayNew = array[0].childPrices.map((price, index) => {
        const sum = array.reduce((accumulator, currentValue, currentIndex) => {
          if (currentIndex === 0) {
            return parseFloat(currentValue.childPrices[index]);
          }
          currentValue.childPrices ??= [];
          return accumulator + parseFloat(currentValue?.childPrices[index]);
        }, parseFloat(price));
        return sum.toFixed(2);
      });
      return childArrayNew;
    }
    return [];
  };

  const pushChildPrices = (el) => {
    const childArray = [];
    if (el) {
      el.forEach((element) => {
        childArray.push({ childPrices: element.childPrices });
      });
      return childArray;
    }
    return [];
  };

  const userAuthorizationsStore = useUserAuthorizationsStore();
  const authorizations: any = userAuthorizationsStore.permissionByRole;
  const canViewPromotion = authorizations?.quote_management?.promotion_view;
  const canViewTermConditions = authorizations?.quote_management?.terms_conditions_view;
  const canEditTermConditions = authorizations?.quote_management?.terms_conditions_edit;
  const canEditPromotion = authorizations?.quote_management?.promotion_edit;
  const canViewAgencyComment = authorizations?.quote_management?.agency_comment_view;
  const canEditAgencyComment = authorizations?.quote_management?.agency_comment_edit;
  const canAdjustOptionMargin = authorizations?.quote_management?.adjust_option_margin_view;
  const canEditAgeRanges = authorizations?.quote_management?.edit_age_ranges;

  const hasUserPermission = (quote, authorizations) => {
    const userRole = toValue(currentUser)?.role;
    const userEmail = toValue(currentUser)?.email;
    const isAssigned = userEmail === quote?.assigneeEmail || userEmail === quote?.coEmail;
    const isDept = userRole === MAP_DEPARTMENT_TO_ROLE[quote?.dept];
    return isAssigned || userRole === GQT_ADMIN || userRole === GQT_SUPERVISOR || isDept || authorizations;
  };

  const validateFlightNumber = (target, field) => {
    const regex = /^\d*$/;
    if (!regex.test(target[field])) {
      target[field] = target[field].replace(/\D/g, '');
    }
  };

  const validateChildInput = (value: string) => {
    const inputValue = parseChildInput(value);
    if (value.includes('()')) {
      return false;
    }
    const [number, text] = inputValue;
    const regexNumberWithText = /^\d+\s*-\s*\d+$/;
    const regexNumberOnly = /^\(\s*\d+\s*-\s*\d+\s*\)$/;
    const regexText = /^(child|enfant)\s+\d+(\s+(and|et)\s+\d+)*$/;
    const isValidNumber = (text && regexNumberWithText.test(number)) || (!text && regexNumberOnly.test(number));
    const isValidText = !text || regexText.test(text);
    return isValidNumber && isValidText;
  };

  const parseChildInput = (str: string) => {
    const valueTrimmed = str.trim();
    const onlyNumber = /^\(\s*\d+\s*-\s*\d+\s*\)$/.exec(valueTrimmed);
    if (onlyNumber) {
      return [valueTrimmed, ''];
    }
    const openParen = valueTrimmed.indexOf('(');
    const closeParen = valueTrimmed.indexOf(')');
    if (openParen !== -1 && closeParen !== -1 && closeParen > openParen) {
      const numberPart = valueTrimmed.slice(0, openParen).trim();
      const textPart = valueTrimmed.slice(openParen + 1, closeParen).trim();
      return [numberPart, textPart];
    }
    return [valueTrimmed, ''];
  };

  const checkEditChildInput = (editorSaveCallback: any, isError: boolean) => {
    if (isError) {
      return handleOpenErrorMessage(i18n.global.t('notification.child_format_error'));
    }
    editorSaveCallback();
    toast.add({ severity: 'success', summary: '', detail: i18n.global.t('notification.save_successfully'), life: 3000 });
  };

  const handleChildInput = (value: any, field: string, indexColumn: any, validationInputs: any) => {
    let isValid = validateChildInput(value);
    if (!indexColumn) {
      const inputKey = `${field}`;
      validationInputs[inputKey] = isValid;
    } else {
      const inputKey = `${field}_${indexColumn}`;
      validationInputs[inputKey] = isValid;
    }
    isValid = Object.values(validationInputs).every((state) => state === true);
    return isValid;
  };

  const airManagementStore = useAirManagementStore();

  const formatLabelAirport = () => {
    return airManagementStore.airs.map((airport) => {
      const airportData = cloneDeep(airport);
      airportData.airportNameLabel = airport.airportName;
      airportData.airportNameLabelFr = airport.airportNameFR;
      airportData.airportName = getTextVariant(currentLanguage, airport.airportName, airport.airportNameFR);
      airportData.countryName = getTextVariant(currentLanguage, airport.countryName, airport.countryNameFr);
      airportData.selectedLabel = `${getTextVariant(currentLanguage, airport.airportName, airport.airportNameFR)} (${airport.airportCode})`;
      return airportData;
    });
  };
  const airportList = computed(() => formatLabelAirport());

  const initFlightData = (type, key) => {
    return {
      type: type,
      gate: '',
      gateCode: '',
      airline: '',
      airlineCode: '',
      flightNo: '',
      flightNumber: '',
      operated: '',
      depDate: new Date(),
      dest: '',
      destCode: '',
      arrDate: new Date(),
      depTime: DateTime.fromJSDate(new Date()).toFormat('T'),
      arrTime: DateTime.fromJSDate(new Date()).toFormat('T'),
      via: null,
      fClass: '',
      cabin: '',
      chd: 0,
      adt: 0,
      direct: false,
      classesAvailable: {},
      stopFlights: [],
      cost: null,
      groupBy: type,
      key,
      isNew: true,
      canDelete: true
    };
  };

  const formatFlightData = (flights: any, isFlightQuote = false) => {
    return flights.map((flight, flightIndex) => {
      flight.key = `flight${Date.now()}${flightIndex}`;
      flight.groupBy = flight.type;
      flight.isNew = false;
      if (isFlightQuote && flight.cabin && typeof flight.cabin === 'string') {
        flight.cabin = flight.cabin.split(', ');
      }
      ['arrDate', 'depDate'].forEach((key) => {
        if (flight[key]) {
          flight[key] = new Date(flight[key]);
        } else {
          flight[key] = new Date();
        }
      });
      return flight;
    });
  };

  const getListDate = (type, typeDate, listFlight) => {
    return listFlight
      .filter((flight) => flight.type === type)
      .map((flight) => {
        return flight[typeDate];
      });
  };

  const checkDate = (listDate, date, type) => {
    if (type === OUTBOUND) {
      return listDate.some((dateItem) => dateItem >= date);
    } else {
      return listDate.some((dateItem) => dateItem <= date);
    }
  };

  const isCheckValidateDate = (newData, flights) => {
    if (newData.type === OUTBOUND) {
      const listArrDateInbound = getListDate(INBOUND, 'arrDate', flights);

      const listDepDateInbound = getListDate(INBOUND, 'depDate', flights);

      return checkDate(listArrDateInbound, newData.arrDate, OUTBOUND) && checkDate(listDepDateInbound, newData.depDate, OUTBOUND);
    } else {
      const listArrDateOutbound = getListDate(OUTBOUND, 'arrDate', flights);

      const listDepDateOutbound = getListDate(OUTBOUND, 'depDate', flights);

      return checkDate(listArrDateOutbound, newData.arrDate, INBOUND) && checkDate(listDepDateOutbound, newData.depDate, INBOUND);
    }
  };
  const onFilterAirport = (airport, event) => {
    return (
      airport.airportName?.toLowerCase().startsWith(event.query.toLowerCase()) ||
      airport.airportCode?.toLowerCase().startsWith(event.query.toLowerCase()) ||
      airport.cityName?.toLowerCase().startsWith(event.query.toLowerCase()) ||
      airport.region?.toLowerCase().startsWith(event.query.toLowerCase()) ||
      airport.countryName?.toLowerCase().startsWith(event.query.toLowerCase()) ||
      airport.province?.toLowerCase().startsWith(event.query.toLowerCase())
    );
  };

  const ECONOMY = 'Y';
  const PREMIUM_ECONOMY = 'O';
  const EXECUTIVE = 'J';

  const LIST_CABIN = [
    {
      name: i18n.global.t('worksheet.economy_class'),
      data: ECONOMY
    },
    {
      name: i18n.global.t('worksheet.premium_economy_class'),
      data: PREMIUM_ECONOMY
    },
    {
      name: i18n.global.t('worksheet.business'),
      data: EXECUTIVE
    }
  ];

  const getCabinName = (key) => {
    return LIST_CABIN.find((cabin) => cabin.data === key)?.name;
  };

  const getCabinType = (keys: string[]) => {
    let nameCabin: string = '';
    if (keys && typeof keys === 'object' && keys.length) {
      keys.forEach((key: string) => {
        const cabinFind = LIST_CABIN_TYPE.find((cabin) => cabin.data.toUpperCase() === key.toUpperCase())?.name ?? '';
        nameCabin = nameCabin ? `${nameCabin}, ${cabinFind}` : cabinFind;
      });
    }
    return nameCabin;
  };

  const formatRowData = (newData: any, originData: any, oldChildValue: any, rowCanAutoCalulations: RowCalculationMap) => {
    const editedRow = originData.rowName;
    const oldData = omit(originData, ['rowName']);
    const changeData: any = {};

    for (const key in oldData) {
      if (key === 'childPrices') {
        const temp = Array(oldData[key].length);
        for (let i = 0; i < oldData[key].length; i++) {
          const newValue = Number(newData[key][i]) || 0;
          const oldValue = oldChildValue ? Number(oldChildValue[i]) : (oldData[key][i] ?? 0);
          temp[i] = newValue - oldValue;
        }
        changeData[key] = temp;
      } else {
        const newValue = Number(newData[key]) || 0;
        const oldValue = Number(oldData[key]) || 0;
        changeData[key] = newValue - oldValue;
      }
    }

    const hasChangeData = Object.values(changeData).some((value) => value);

    return {
      rowCanAutoCalulations,
      editedRow,
      changeData,
      hasChangeData
    };
  };

  const formatDataBeforeSaveRow = (newData: any, originData: any, oldChildValue?: any) => {
    return formatRowData(newData, originData, oldChildValue, {
      sellingPrice: 'sellingPrice',
      marginAdjustment: 'marAdj',
      costAdjustment: 'costAdj',
      flCost: 'flCost',
      flCostOffset: 'flCostOffset',
      flCostAdjustment: 'flCostAdjustment'
    });
  };

  const formatDataCostAdjustment = (newData: any, originData: any, oldChildValue: any) => {
    return formatRowData(newData, originData, oldChildValue, {
      supplierCost: 'supplierCost',
      supplierTax: 'supplierTax',
      supplierSupplement: 'supplierSupplement',
      supplierCostCAD: 'supplierCostCAD',
      hotelCostCAD: 'hotelCostCAD'
    });
  };

  const handleRedirectLink = (event, link) => {
    if (event.shiftKey) {
      window.open(link, '_blank');
    } else {
      location.href = link;
    }
  };

  const checkDateQuote = (depDate: any, dateQuoted?: any) => {
    const today = dateQuoted ? new Date(dateQuoted) : new Date();
    today.setDate(today.getDate() + 346);
    return new Date(depDate).getTime() < today.getTime();
  };

  const checkFirstNumber = (inputText: string) => {
    const openingParenIndex = inputText.indexOf('(');
    const closeParenIndex = inputText.indexOf(')');
    const dashIndex = inputText.indexOf('-');
    if (openingParenIndex !== -1 && dashIndex !== -1 && openingParenIndex === 0) {
      const numberString = inputText.substring(dashIndex + 1, closeParenIndex).trim();
      const firstNumber = +numberString;
      return firstNumber >= 12;
    } else if (openingParenIndex !== -1 && dashIndex !== -1) {
      const numberString = inputText.substring(dashIndex + 1, openingParenIndex).trim();
      const firstNumber = +numberString;
      return firstNumber >= 12;
    } else {
      return false;
    }
  };

  const getTotalFare = (tax: any, key: string) => {
    let total = 0;
    if (tax.length) {
      total = tax.reduce(function (acc: any, obj: any) {
        return acc + (obj?.[key] && obj?.airTaxCode != 'FARE_BASE' ? +obj?.[key] : 0);
      }, 0);
    }
    return total.toFixed(2);
  };

  const getBaseFare = (taxes: any, oldData: any) => {
    const objSellingPrice = oldData?.find((obj: any) => obj?.rowName === 'sellingPrice');
    const objAgencyTaxesPrice = oldData?.find((obj: any) => obj?.rowName === 'taxes');
    const objFilterChildEdit = oldData?.find((obj: any) => obj?.rowName === 'occupanciesEdit');
    const objTotalSell = oldData?.find((obj: any) => obj?.rowName === 'priceSummary');
    const objBase = oldData?.find((obj: any) => obj?.rowName === 'ardBaseFare');
    if (!objSellingPrice) {
      return [...oldData];
    }
    if (objBase) {
      taxes?.forEach((tax: any) => {
        if (tax?.airTaxCode == 'FARE_BASE') {
          objBase.canEdit = false;
          objBase.rowName = 'ardBaseFare';
          const priceKeys = ['singlePrice', 'doublePrice', 'triplePrice', 'quadPrice', 'fiveInRoomPrice', 'sixInRoomPrice'];
          const childPriceKeys = ['childOnePrice', 'childTwoPrice', 'childThreePrice'];
          priceKeys.forEach((price) => (objBase[price] = tax.adtFare));
          ['infantPrice'].forEach((price) => (objBase[price] = tax.inFare));
          childPriceKeys.forEach((price) => (objBase[price] = tax.chFare));
          objBase.childPrices = Array(oldData[0].childPrices.length).fill(tax.chFare);
          const temp = Array(objTotalSell.childPrices.length);
          for (let i = 0; i < objTotalSell.childPrices.length; i++) {
            if (objFilterChildEdit?.childPrices[i] && checkFirstNumber(objFilterChildEdit?.childPrices[i])) {
              temp[i] = tax.adtFare;
            } else {
              temp[i] = tax.chFare;
            }
          }
          for (let i = 0; i < objSellingPrice.childPrices.length; i++) {
            const sellingChildPriceVal = objSellingPrice?.childPrices?.[i];
            const agencyTaxesChildPriceVal = objAgencyTaxesPrice?.childPrices?.[i];

            if (isInvalidValuePrice(sellingChildPriceVal) && isInvalidValuePrice(agencyTaxesChildPriceVal)) {
              temp[i] = '0.00';
            }
          }
          objBase.childPrices = temp;
        }
      });
      Object.keys(objSellingPrice).forEach((key) => {
        const sellingPriceVal = objSellingPrice[key];
        const agencyTaxesPriceVal = objAgencyTaxesPrice?.[key];
        if (isInvalidValuePrice(sellingPriceVal) && isInvalidValuePrice(agencyTaxesPriceVal)) {
          objBase[key] = '0.00';
        }
      });
    }

    return oldData;
  };

  const convertTaxes346ToNumber = (data: any) => {
    let prices = [data?.singlePrice, data?.doublePrice, data?.triplePrice, data?.quadPrice, data?.fiveInRoomPrice, data?.sixInRoomPrice, data?.infantPrice, data?.childOnePrice, data?.childTwoPrice, data?.childThreePrice];
    prices = prices.map((price) => parseFloat(price)).filter((price) => price !== 0.0);
    const uniquePrices = [...new Set(prices)];
    return uniquePrices[0];
  };

  const handleParseFloat = (price: number) => {
    return parseFloat(price?.toFixed(2));
  };

  const handleBreakTax = (classSelect: any, taxType: string, basePriceType: string) => {
    return [
      ...(classSelect.data[taxType] ?? []),
      {
        airTaxCode: 'FARE_BASE',
        airTaxType: null,
        airTaxDes: null,
        adtFare: classSelect.data[basePriceType]
      }
    ];
  };

  const breakdownPrice = (classSelect: any) => {
    switch (classSelect.data.suffixKey) {
      case SUFFIX_PUBLIC:
        return handleBreakTax(classSelect, 'publishedTax', 'basePublishedPrice');
      case SUFFIX_GROUPB:
        return handleBreakTax(classSelect, 'groupsBTax', 'baseGroupsBPrice');
      case SUFFIX_VACV:
        return handleBreakTax(classSelect, 'vacTax', 'baseVacPrice');
      default:
        return [];
    }
  };

  const checkConditionTotal = (depDate: any, value: any, ardTax: any, dateQuoted?: any) => {
    return !checkDateQuote(depDate, dateQuoted) ? convertTaxes346ToNumber(ardTax || 0) : value;
  };

  const getArdTaxes = (taxes: any, oldData: any, depDate: any, ardTax: any, dateQuoted?: any) => {
    const objSellingPrice = oldData?.find((obj: any) => obj?.rowName === 'sellingPrice');
    const objAgencyTaxesPrice = oldData?.find((obj: any) => obj?.rowName === 'taxes');
    const objFilterChildEdit = oldData?.find((obj: any) => obj?.rowName === 'occupanciesEdit');
    const objTotalSell = oldData?.find((obj: any) => obj?.rowName === 'priceSummary');
    const objTaxes = oldData?.find((obj: any) => obj?.rowName === 'ardTaxes');
    const totalAdt = checkConditionTotal(depDate, getTotalFare(taxes, 'adtFare'), ardTax, dateQuoted);
    const totalCh = checkConditionTotal(depDate, getTotalFare(taxes, 'chFare'), ardTax, dateQuoted);
    const totalInf = checkConditionTotal(depDate, getTotalFare(taxes, 'inFare'), ardTax, dateQuoted);
    if (!objSellingPrice) {
      return [...oldData];
    }
    if (objTaxes) {
      objTaxes.canEdit = false;
      objTaxes.rowName = 'ardTaxes';
      const priceKeys = ['singlePrice', 'doublePrice', 'triplePrice', 'quadPrice', 'fiveInRoomPrice', 'sixInRoomPrice'];
      const childPriceKeys = ['childOnePrice', 'childTwoPrice', 'childThreePrice'];
      priceKeys.forEach((price) => (objTaxes[price] = totalAdt));
      ['infantPrice'].forEach((price) => (objTaxes[price] = totalInf));
      childPriceKeys.forEach((price) => (objTaxes[price] = totalCh));
      objTaxes.childPrices = Array(oldData[0].childPrices.length).fill(totalCh);
      const temp = Array(objTotalSell.childPrices.length);
      for (let i = 0; i < objTotalSell.childPrices.length; i++) {
        if (objFilterChildEdit?.childPrices[i] && checkFirstNumber(objFilterChildEdit?.childPrices[i])) {
          temp[i] = totalAdt;
        } else {
          temp[i] = totalCh;
        }
      }
      for (let i = 0; i < objSellingPrice.childPrices.length; i++) {
        const sellingChildPriceVal = objSellingPrice?.childPrices?.[i];
        const agencyTaxesChildPriceVal = objAgencyTaxesPrice?.childPrices?.[i];

        if (isInvalidValuePrice(sellingChildPriceVal) && isInvalidValuePrice(agencyTaxesChildPriceVal)) {
          temp[i] = '0.00';
        }
      }
      objTaxes.childPrices = temp;
      Object.keys(objSellingPrice).forEach((key) => {
        const sellingPriceVal = objSellingPrice[key];
        const agencyTaxesPriceVal = objAgencyTaxesPrice?.[key];
        if (isInvalidValuePrice(sellingPriceVal) && isInvalidValuePrice(agencyTaxesPriceVal)) {
          objTaxes[key] = '0.00';
        }
      });
    }

    return oldData;
  };

  const getNetMargin = (taxes: any, oldData: any, depDate: any, ardTax?: any, dateQuoted?: any) => {
    const objSellingPrice = oldData?.find((obj: any) => obj?.rowName === 'sellingPrice');
    const objAgencyTaxesPrice = oldData?.find((obj: any) => obj?.rowName === 'taxes');
    const objFilterChildEdit = oldData?.find((obj: any) => obj?.rowName === 'occupanciesEdit');
    const objNetMargin = oldData?.find((obj: any) => obj?.rowName === 'netMargin');
    const totalAdt = checkConditionTotal(depDate, getTotalFare(taxes, 'adtFare'), ardTax, dateQuoted);
    const totalCh = checkConditionTotal(depDate, getTotalFare(taxes, 'chFare'), ardTax, dateQuoted);
    const totalInf = checkConditionTotal(depDate, getTotalFare(taxes, 'inFare'), ardTax, dateQuoted);
    const objTotalSell = oldData?.find((obj: any) => obj?.rowName === 'priceSummary');
    const objTotalCost = oldData?.find((obj: any) => obj?.rowName === 'costSummary');
    if (!objSellingPrice) {
      return [...oldData];
    }
    if (objNetMargin) {
      objNetMargin.canEdit = false;
      objNetMargin.rowName = 'netMargin';
      objNetMargin.singlePrice = (+objTotalSell?.singlePrice - +objTotalCost?.singlePrice - +totalAdt).toFixed(2);
      objNetMargin.doublePrice = (+objTotalSell?.doublePrice - +objTotalCost?.doublePrice - +totalAdt).toFixed(2);
      objNetMargin.triplePrice = (+objTotalSell?.triplePrice - +objTotalCost?.triplePrice - +totalAdt).toFixed(2);
      objNetMargin.quadPrice = (+objTotalSell?.quadPrice - +objTotalCost?.quadPrice - +totalAdt).toFixed(2);
      objNetMargin.fiveInRoomPrice = (+objTotalSell?.fiveInRoomPrice - +objTotalCost?.fiveInRoomPrice - +totalAdt).toFixed(2);
      objNetMargin.sixInRoomPrice = (+objTotalSell?.sixInRoomPrice - +objTotalCost?.sixInRoomPrice - +totalAdt).toFixed(2);
      objNetMargin.infantPrice = (+objTotalSell?.infantPrice - +objTotalCost?.infantPrice - +totalInf).toFixed(2);
      objNetMargin.childOnePrice = (+objTotalSell?.childOnePrice - +objTotalCost?.childOnePrice - +totalCh).toFixed(2);
      objNetMargin.childTwoPrice = (+objTotalSell?.childTwoPrice - +objTotalCost?.childTwoPrice - +totalCh).toFixed(2);
      objNetMargin.childThreePrice = (+objTotalSell?.childThreePrice - +objTotalCost?.childThreePrice - +totalCh).toFixed(2);
      const temp = Array(objTotalSell.childPrices.length);
      for (let i = 0; i < objTotalSell.childPrices.length; i++) {
        if (objFilterChildEdit?.childPrices[i] && checkFirstNumber(objFilterChildEdit?.childPrices[i])) {
          temp[i] = (+objTotalSell?.childPrices[i] - +objTotalCost?.childPrices[i] - +totalAdt).toFixed(2);
        } else {
          temp[i] = (+objTotalSell?.childPrices[i] - +objTotalCost?.childPrices[i] - +totalCh).toFixed(2);
        }
      }
      for (let i = 0; i < objSellingPrice.childPrices.length; i++) {
        const sellingChildPriceVal = objSellingPrice?.childPrices?.[i];
        const agencyTaxesChildPriceVal = objAgencyTaxesPrice?.childPrices?.[i];

        if (isInvalidValuePrice(sellingChildPriceVal) && isInvalidValuePrice(agencyTaxesChildPriceVal)) {
          temp[i] = '0.00';
        }
      }
      objNetMargin.childPrices = temp;
      Object.keys(objSellingPrice).forEach((key) => {
        const sellingPriceVal = objSellingPrice[key];
        const agencyTaxesPriceVal = objAgencyTaxesPrice?.[key];
        if (isInvalidValuePrice(sellingPriceVal) && isInvalidValuePrice(agencyTaxesPriceVal)) {
          objNetMargin[key] = '0.00';
        }
      });
    }

    return oldData;
  };

  const getMarAdjAndSellingPriceAndTotalMar = (valueChangeMargin: any, key: string, cloneValue: any, changeData: any) => {
    const tempMarAdj = Array(valueChangeMargin[key].length).fill(0);
    const tempSellingPrice = Array(valueChangeMargin[key].length).fill(0);
    const tempTotalMar = Array(valueChangeMargin[key].length).fill(0);
    for (let i = 0; i < valueChangeMargin[key].length; i++) {
      if (cloneValue?.sellingPrice?.[key] && changeData && cloneValue.sellingPrice[key][i]) {
        tempMarAdj[i] = Number(cloneValue.sellingPrice[key][i]) ? Number(cloneValue.marAdj[key][i]) + changeData[key][i] : Number(cloneValue.marAdj[key][i]);
        tempSellingPrice[i] = Number(cloneValue.sellingPrice[key][i]) ? Number(cloneValue.sellingPrice[key][i]) + changeData[key][i] : Number(cloneValue.sellingPrice[key][i]);
        tempTotalMar[i] = Number(cloneValue.sellingPrice[key][i]) ? Number(cloneValue.totalMar[key][i]) + changeData[key][i] : Number(cloneValue.totalMar[key][i]);
      }
    }
    return { tempMarAdj: tempMarAdj, tempSellingPrice: tempSellingPrice, tempTotalMar: tempTotalMar };
  };

  const handleConvertBrTag = (inputString: string) => {
    return inputString.replace(/<br>/g, '<br/>');
  };

  const renderPriceColumn = (permission: any) => {
    const priceColumn = [];
    if (permission?.agency_selling_price_view) {
      priceColumn.push({
        key: SUFFIX_AGENCY_SELLING_PRICE,
        canEdit: permission?.agency_selling_price_edit
      });
    }
    if (permission?.agency_tax_view) {
      priceColumn.push({
        key: SUFFIX_TAX,
        canEdit: permission?.agency_tax_edit
      });
    }
    if (permission?.total_selling_price_view) {
      priceColumn.push({
        key: SUFFIX_TOTAL_SELLING,
        canEdit: permission?.total_selling_price_edit
      });
    }
    if (permission?.published_rate_view) {
      priceColumn.push({
        key: SUFFIX_PUBLIC,
        canEdit: permission?.published_rate_edit
      });
    }
    if (permission?.groupB_pricing_view) {
      priceColumn.push({
        key: SUFFIX_GROUPB,
        canEdit: permission?.groupB_pricing_edit
      });
    }
    if (permission?.vacv_pricing_view) {
      priceColumn.push({
        key: SUFFIX_VACV,
        canEdit: permission?.vacv_pricing_edit
      });
    }
    if (permission?.charter_pricing_view) {
      priceColumn.push({
        key: SUFFIX_CHARTER,
        canEdit: permission?.charter_pricing_edit
      });
    }
    return priceColumn;
  };

  const formatPriceRow = (item: any, permission: any) => {
    const formatDataPrice = renderPriceColumn(permission).map((suffix, index) => {
      let fare = 0;
      let rowName = '';
      let key = '';
      let adt = 0;
      let chd = 0;
      let paxFareSumAdult = 0;
      let paxFareSumChild = 0;
      let adtTax = 0;
      let chdTax = 0;
      switch (suffix.key) {
        case SUFFIX_AGENCY_SELLING_PRICE:
          fare = item.fare;
          adt = item.adt;
          adtTax = item.adtTax;
          chd = item.chd;
          chdTax = item.chdTax;
          paxFareSumAdult = item.adt + item.adtTax;
          paxFareSumChild = item.chd + item.chdTax;
          rowName = 'worksheet.agency_selling_price';
          break;
        case SUFFIX_TAX:
          fare = item.fareTax;
          adtTax = item.adtTax;
          chdTax = item.chdTax;
          rowName = 'worksheet.agency_taxes';
          break;
        case SUFFIX_PUBLIC:
          fare = item.farePub;
          rowName = 'worksheet.published_rate';
          break;
        case SUFFIX_GROUPB:
          fare = item.fareGroupsB;
          rowName = 'worksheet.groupsb_pricing';
          break;
        case SUFFIX_VACV:
          fare = item.fareVACV;
          rowName = 'worksheet.vacv_pricing';
          break;
        case SUFFIX_CHARTER:
          fare = item.fareCharter;
          rowName = 'worksheet.charter';
          break;
        case SUFFIX_TOTAL_SELLING:
          rowName = 'worksheet.total_selling';
          key = 'total';
          break;
        default:
          break;
      }
      return {
        canEdit: suffix.canEdit,
        key,
        rowName,
        index,
        suffix: suffix.key,
        detail: index <= 1 ? item.detail : '',
        fare,
        adt,
        chd,
        paxFareSumAdult,
        paxFareSumChild,
        adtTax,
        chdTax,
        inf: item[`inf${suffix.key}`],
        suffixKey: item.suffixKey,
        breakdownPrice: item.breakdownPrice
      };
    });
    const totalRow = formatDataPrice.find((row) => row.rowName === 'worksheet.total_selling');
    const targetTotal = formatDataPrice.filter((item) => item.rowName === 'worksheet.agency_selling_price' || item.rowName === 'worksheet.agency_taxes');
    if (totalRow) {
      totalRow.fare = targetTotal.reduce((accumulator, currentValue) => accumulator + Number(currentValue.fare), 0);
      totalRow.adt = targetTotal.reduce((accumulator, currentValue) => accumulator + Number(currentValue.paxFareSumAdult), 0);
      totalRow.chd = targetTotal.reduce((accumulator, currentValue) => accumulator + Number(currentValue.paxFareSumChild), 0);
      totalRow.inf = targetTotal.reduce((accumulator, currentValue) => accumulator + Number(currentValue.inf), 0);
    }
    const agencySellingPrice = formatDataPrice.find((row) => row.rowName === 'worksheet.agency_selling_price');
    const agencyTaxes = formatDataPrice.find((row) => row.rowName === 'worksheet.agency_taxes');
    const targetSellingPrice = formatDataPrice.filter((item) => item.rowName === 'worksheet.agency_selling_price');
    const targetTaxes = formatDataPrice.filter((item) => item.rowName === 'worksheet.agency_taxes');
    if (agencySellingPrice) {
      agencySellingPrice.fare = targetSellingPrice.reduce((accumulator, currentValue) => accumulator + Number(currentValue.fare), 0);
      agencySellingPrice.adt = targetSellingPrice.reduce((accumulator, currentValue) => accumulator + Number(currentValue.adt), 0);
      agencySellingPrice.chd = targetSellingPrice.reduce((accumulator, currentValue) => accumulator + Number(currentValue.chd), 0);
    }
    if (agencyTaxes) {
      agencyTaxes.fare = targetTaxes.reduce((accumulator, currentValue) => accumulator + Number(currentValue.fare), 0);
      agencyTaxes.adt = targetTaxes.reduce((accumulator, currentValue) => accumulator + Number(currentValue.adtTax), 0);
      agencyTaxes.chd = targetTaxes.reduce((accumulator, currentValue) => accumulator + Number(currentValue.chdTax), 0);
    }
    return formatDataPrice;
  };

  const changePricingFlight = ({ newData, index }: { newData: any; index: number }, price: any, rows: any) => {
    const suffix = rows[index].suffix;
    let fareMinus = 0;
    switch (suffix) {
      case SUFFIX_AGENCY_SELLING_PRICE:
        price.fare = newData.fare;
        price.adt = newData.adt;
        price.chd = newData.chd;
        price.paxFareSumAdult = price.adt + price.adtTax;
        price.paxFareSumChild = price.chd + price.chdTax;
        price.detail = newData.detail;
        break;
      case SUFFIX_TAX:
        price.adtTax = handleParseFloat(newData.adt);
        price.chdTax = handleParseFloat(newData.chd);
        price.paxFareSumAdult = price.adt + price.adtTax;
        price.paxFareSumChild = price.chd + price.chdTax;
        price.detail = newData.detail;
        break;
      case SUFFIX_PUBLIC:
        fareMinus = Number(newData.fare - price.farePub);
        price.farePub = newData.fare;
        if (price.suffixKey === SUFFIX_PUBLIC) {
          price.fareTax = handleParseFloat(price.farePub - (price.basePrice ?? price.fare));
          price.adtTax = handleParseFloat(price.adtTax + fareMinus);
          price.chdTax = handleParseFloat(price.chdTax + fareMinus);
          price.paxFareSumAdult = price.adt + price.adtTax;
          price.paxFareSumChild = price.chd + price.chdTax;
        }
        break;
      case SUFFIX_GROUPB:
        fareMinus = Number(newData.fare - price.fareGroupsB);
        price.fareGroupsB = newData.fare;
        if (price.suffixKey === SUFFIX_GROUPB) {
          price.fareTax = handleParseFloat(price.fareGroupsB - (price.basePrice ?? price.fare));
          price.adtTax = handleParseFloat(price.adtTax + fareMinus);
          price.chdTax = handleParseFloat(price.chdTax + fareMinus);
          price.paxFareSumAdult = price.adt + price.adtTax;
          price.paxFareSumChild = price.chd + price.chdTax;
        }
        break;
      case SUFFIX_VACV:
        fareMinus = Number(newData.fare - price.fareVACV);
        price.fareVACV = newData.fare;
        if (price.suffixKey === SUFFIX_VACV) {
          price.fareTax = handleParseFloat(price.fareVACV - (price.basePrice ?? price.fare));
          price.adtTax = handleParseFloat(price.adtTax + fareMinus);
          price.chdTax = handleParseFloat(price.chdTax + fareMinus);
          price.paxFareSumAdult = price.adt + price.adtTax;
          price.paxFareSumChild = price.chd + price.chdTax;
        }
        break;
      case SUFFIX_CHARTER:
        price.fareCharter = newData.fare;
        break;
      default:
        break;
    }
    ['adt', 'chd', 'inf'].forEach((key) => {
      price[`${key}${suffix}`.trim()] = newData[`${key}`.trim()];
    });
  };

  const isInvalidValuePrice = (value: any) => {
    return !value || value === null || value === undefined || value === '0.00' || value === '0.0' || value === 'undefined' || value === 'null' || value === '';
  };

  return {
    filterPriceList,
    filterCostAdjustment,
    PRICE_KEY,
    titleHotel,
    renderPriceSummary,
    renderAdjustmentSummary,
    renderCostSummary,
    renderAdditionInformation,
    renderSupplierCostSummary,
    renderSupplierCostCADSummary,
    filterPermission,
    filterPermissionCostAdjusmentTool,
    authorizations,
    canViewPromotion,
    canEditPromotion,
    canViewAgencyComment,
    canEditAgencyComment,
    canViewTermConditions,
    canEditTermConditions,
    canAdjustOptionMargin,
    hasUserPermission,
    loadingIcon,
    validateFlightNumber,
    airportList,
    initFlightData,
    formatFlightData,
    onFilterAirport,
    LIST_CABIN,
    getCabinName,
    formatRowData,
    formatDataBeforeSaveRow,
    formatDataCostAdjustment,
    semistarImage,
    handleRedirectLink,
    getListDate,
    checkDate,
    isCheckValidateDate,
    responseTotalArrayChild,
    pushChildPrices,
    titleCostAdjustmentTool,
    calculateCostAdjustmentForChild,
    calculateListSupplier,
    getCabinType,
    LIST_CABIN_TYPE,
    formatLabelAirport,
    checkFirstNumber,
    checkDateQuote,
    getNetMargin,
    getBaseFare,
    getArdTaxes,
    getMarAdjAndSellingPriceAndTotalMar,
    handleConvertBrTag,
    handleParseFloat,
    breakdownPrice,
    formatPriceRow,
    changePricingFlight,
    validateChildInput,
    checkEditChildInput,
    handleChildInput,
    isInvalidValuePrice
  };
}
